import { useEffect, useRef, useState } from 'react';
import { WAYBILL_CODE_MIN } from '@/domain';
import { strings } from '@/shared/strings';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { modalStyles } from '@/ui/primitives/Modal';

export interface TrackingDialogProps {
  jobOrderNo: string;
  /** The courier that collected it, shown above the field and not changed here. */
  carrier: string | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (trackingCode: string) => void;
}

/**
 * The waybill code, the morning after the courier collected. Request of 13
 * September 2026, section 1.6.
 *
 * This is the dialog somebody opens twelve times on a Tuesday morning, so it is
 * one field, focused on open, and Enter saves it. The screen closes it when the
 * server agrees. The code is typed by hand from the waybill and never generated.
 *
 * Since the request of 5 October 2026 (third), section 2, the dialog is itself
 * the confirmation, on the shared `ConfirmSheet`: the question above the field
 * repeats the code as it is typed, and Save code is the answer. It is still one
 * step, never a second question on top. Enter and the button send the same
 * code once between them.
 */
export function TrackingDialog({ jobOrderNo, carrier, busy, onCancel, onConfirm }: TrackingDialogProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Enter does not pass through the sheet's own lock, so the dialog keeps one of its own.
  const sent = useRef(false);
  useEffect(() => {
    if (!busy) sent.current = false;
  }, [busy]);

  function confirm(): boolean {
    if (busy || sent.current) return true;
    const trimmed = code.trim();
    if (trimmed.length < WAYBILL_CODE_MIN) {
      setError(strings.shipping.codeTooShort);
      return false;
    }
    sent.current = true;
    onConfirm(trimmed);
    return true;
  }

  return (
    <ConfirmSheet
      title={`${strings.shipping.trackingTitle} ${jobOrderNo}`}
      statement={strings.confirm.saveTracking(jobOrderNo, code.trim())}
      confirmLabel={strings.shipping.trackingSave}
      cancelLabel={strings.shipping.cancel}
      closeLabel={strings.sheet.close}
      onConfirm={confirm}
      onCancel={onCancel}
      busy={busy}
    >
      {carrier !== null && (
        <div className={modalStyles.field}>
          <span className={modalStyles.label}>{strings.dispatch.carrier}</span>
          <span>{carrier}</span>
        </div>
      )}

      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor="tracking-code">
          {strings.shipping.trackingCode}
        </label>
        <input
          id="tracking-code"
          className={modalStyles.input}
          value={code}
          placeholder={strings.dispatch.codePlaceholder}
          autoComplete="off"
          data-autofocus
          onChange={(event) => {
            setCode(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') confirm();
          }}
        />
        <span className={modalStyles.hint}>{strings.shipping.trackingLead}</span>
      </div>

      {error !== null && (
        <p className={modalStyles.error} role="alert">
          {error}
        </p>
      )}
    </ConfirmSheet>
  );
}
